import React, { useContext, useReducer, useRef } from 'react'

import scrollIntoView from 'scroll-into-view'

import { userContext } from 'global/context/sub-contexts/userContext'
import { legacyImageURL } from 'components/LegacyImage'

import IntersectionVisible from 'components/IntersectionVisible'

import ErrorScreen from './ErrorScreen'
import ProductSelectorScreen from './ProductSelectorScreen'
import SubmitProductReviewScreen from './SubmitProductReviewScreen'
import SubmitProductReviewThankYouScreen from './SubmitProductReviewThankYouScreen'
import ReminderScreen from './ReminderScreen'
import ReminderThankYouScreen from './ReminderThankYouScreen'

import {
  reminder as submitReminder,
  submit as submitReview,
} from 'services/review'
import analytics from 'global/AnalyticsData'

/*

Areas of confusion
products have 2 ids:
    `id` - an internal beaker id that we NEVER use
    `productId` - an id that Bazaarvoice uses that we ALWAYS use

*/

function reducer(state, action) {
  // console.log("action type: " + action.type)
  switch (action.type) {
    case 'step-start':
      return {
        nonce: action.nonce,
        screen: 'product-selector',
        arrowVisible: false,
        skipVisible: true,
      }

    case 'product-selected':
      return {
        nonce: state.nonce,
        screen: 'product-review',
        productId: action.productId,
        arrowVisible: false,
        skipVisible: true,
      }

    case 'product-skipped':
      return {
        nonce: state.nonce,
        screen: 'reminder',
        arrowVisible: false,
        skipVisible: true,
        email: action.email,
      }

    case 'product-review-submitted':
      return {
        nonce: state.nonce,
        screen: 'product-review-thankyou',
        productId: action.productId,
        arrowVisible: true,
        skipVisible: false,
      }

    case 'reminder-submit':
      return {
        nonce: state.nonce,
        screen: 'reminder-thankyou',
        productId: action.productId,
        productTitle: action.productTitle,
        productImageUrl: action.productImageUrl,
        email: action.email,
        arrowVisible: true,
        skipVisible: false,
      }

    case 'error':
      return {
        nonce: state.nonce,
        screen: 'error',
        error: action.error,
        arrowVisible: true,
        skipVisible: false,
      }

    default:
      console.warn(`Unsupported action.type: ${action.type}`)
  }
}

const ReviewStep = ({
  brk,
  breakId,
  step,
  onEngage = function () {},
  onAwardPoints = function () {},
  nextStepButton,
  skipButton,
}) => {
  const { getProfile } = useContext(userContext)

  let initialState = {
    screen: 'product-selector',
    arrowVisible: false,
    skipVisible: false,
  }

  const [state, dispatch] = useReducer(reducer, initialState)

  const selfRef = useRef(null)

  const setIsWaiting = isWaiting => {
    if (isWaiting) {
      selfRef.current.classList.remove('waiting-false')
      selfRef.current.classList.add('waiting-true')
    } else {
      selfRef.current.classList.remove('waiting-true')
      selfRef.current.classList.add('waiting-false')
    }
  }

  const scrollToTopOfStepUI = () => {
    return new Promise(function (resolve) {
      scrollIntoView(selfRef.current, {}, () => {
        resolve()
      })
    })
  }

  const handleProductSelect = async (id, label) => {
    await scrollToTopOfStepUI()

    try {
      dispatch({
        type: 'product-selected',
        productId: id,
      })
    } catch {
      console.log('error rednering products')
    }
  }

  // user did not select a product to review
  const handleProductSkip = async () => {
    await scrollToTopOfStepUI()

    // want want to pre-fill the email form field
    // so we look it up here and pass it in
    let profile = getProfile()
    dispatch({
      type: 'product-skipped',
      email: profile.guest ? '' : profile.email,
    })
  }

  // user submitted a review
  const handleProductReviewSubmit = async (
    productId,
    rating,
    headline,
    comments,
    nickname,
    email,
    ioBlackBox,
    reviewSessionId,
  ) => {
    onEngage()
    onAwardPoints()

    const product = step.review.products.find(product => {
      return product.productId === productId
    })

    // try {
    setIsWaiting(true)
    let response = await submitReview(
      breakId,
      step.review.id,
      productId,
      rating,
      headline,
      comments,
      nickname,
      email,
      ioBlackBox,
      reviewSessionId,
    )
    analytics.stepEvent('step_interaction_review_submit', step, {
      result_id: productId,
      result: product.productSelectorTitle,
    })

    setIsWaiting(false)

    if (response.status === 200) {
      await scrollToTopOfStepUI()

      dispatch({
        type: 'product-review-submitted',
        productId,
        rating,
        headline,
        comments,
        nickname,
        email,
      })
    } else if (response.status === 400) {
      // console.log(response.status);
      // let json = JSON.parse(body)
      let json = undefined
      let responseText = await response.text()
      try {
        json = JSON.parse(responseText)
      } catch (e) {
        // we got an error, but not well-formed json. show it as a general form error.
        // hack: formik has no concept of generic form error messages
        // so attach this error to the bottom-most field, shaped like we expect.
        json = {
          usernickname: {
            Message: responseText,
          },
        }
      }
      return json
    } else {
      await scrollToTopOfStepUI()

      dispatch({
        type: 'error',
        error: response,
      })
    }
  }

  // visitor opted in to receiving a reminder
  const handleReminderSubmit = async (email, productId, turnstileToken) => {
    onEngage()
    onAwardPoints()

    console.log('handleReminderSubmit ' + productId + ' ' + email)

    const product = step.review.products.find(product => {
      return product.productId === productId
    })

    try {
      setIsWaiting(true)
      await submitReminder(breakId, step.review.id, productId, email, turnstileToken)
      analytics.stepEvent('step_interaction_reminder_submit', step, {
        result_id: productId,
        result: product.productSelectorTitle,
      })

      await scrollToTopOfStepUI()

      setIsWaiting(false)

      dispatch({
        type: 'reminder-submit',
        productId: product.productId,
        productTitle: product.title,
        productImageUrl: legacyImageURL(product.image),
        email: email,
      })
    } catch (e) {
      await scrollToTopOfStepUI()

      setIsWaiting(false)

      dispatch({
        type: 'error',
        error: e.detail, // we assume the server sent a 4xx with detail
      })
    }
  }

  let style = {}
  const bgImg =
    step && step.backgroundImage
      ? step.backgroundImage.id
      : step.backgroundImageId

  if (step && step.backgroundColor) {
    style.backgroundColor = step.backgroundColor
  }

  if (bgImg) {
    style.backgroundImage = `url(${process.env.SITE_ASSET_URL}/${bgImg})`
  }

  return (
    <section className={`review-step continue-ready`} ref={selfRef}>
      <div
        className={`step-content with-shadow ${
          brk?.navigationType === 'TOP' ? 'top-navigation' : ''
        }`}
      >
        {state.screen === 'product-selector' && (
          <ProductSelectorScreen
            review={step.review}
            onSelect={handleProductSelect}
            onSkip={handleProductSkip}
          />
        )}
        {state.screen === 'reminder' && (
          <ReminderScreen
            review={step.review}
            defaultProductId={state.productId}
            defaultEmail={state.email}
            onSubmit={handleReminderSubmit}
          />
        )}
        {state.screen === 'reminder-thankyou' && (
          <ReminderThankYouScreen
            review={step.review}
            email={state.email}
            productTitle={state.productTitle}
            productImageUrl={state.productImageUrl}
            reminderDate={state.reminderDate}
          />
        )}
        {state.screen === 'product-review' && (
          <SubmitProductReviewScreen
            review={step.review}
            defaultProductId={state.productId}
            onFormSubmit={handleProductReviewSubmit}
          />
        )}
        {state.screen === 'product-review-thankyou' && (
          <SubmitProductReviewThankYouScreen
            review={step.review}
            productId={state.productId}
          />
        )}

        {state.screen === 'error' && (
          <ErrorScreen review={step.review} error={state.error} />
        )}
        {skipButton}
        {nextStepButton}
      </div>
    </section>
  )
}

export default ReviewStep
